import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import LandingPage from "@/pages/LandingPage";
import AdminPage from "@/pages/AdminPage";
import AboutPage from "@/pages/AboutPage";
import TeamPage from "@/pages/TeamPage";
import TermsPage from "@/pages/TermsPage";
import PressPage from "@/pages/PressPage";
import AccountPage from "@/pages/AccountPage";
import { TalentPage, CareersPage } from "@/pages/ApplyPages";
import RosterPage from "@/pages/RosterPage";
import MedusaRoomPage from "@/pages/MedusaRoomPage";
import MedusaGuidePage from "@/pages/MedusaGuidePage";
import MedusaTermsPage from "@/pages/MedusaTermsPage";
import PianoMasteryPage from "@/pages/PianoMasteryPage";
import AcademyPage from "@/pages/AcademyPage";
import Studio3DPage from "@/pages/Studio3DPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import VipVaultPage from "@/pages/VipVaultPage";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { SiteAuthProvider } from "@/components/GoogleAuth";
import { AuthCallback } from "@/components/AuthCallback";
import { ChatWidget } from "@/components/ChatWidget";
import { AppSplash } from "@/components/AppSplash";
import {
  ServicesPage, BeatsPage, MerchPage, BookingPage, QuotePage, FAQPage,
} from "@/pages/SectionPages";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -90, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

const GlobalHaze = () => {
  const { pathname } = useLocation();
  // the Medusa Room has its own textured smoke — everywhere else gets the light haze
  if (pathname === "/medusa" || pathname.startsWith("/admin")) return null;
  return (
    <>
      <style>{`
        @keyframes globalSmokeDrift {
          0% { transform: translateY(10px) scaleX(1); opacity: .35; }
          50% { transform: translateY(0) scaleX(1.05); opacity: .55; }
          100% { transform: translateY(14px) scaleX(.96); opacity: .42; }
        }
        .global-purple-smoke {
          position: fixed; bottom: 0; left: 0; width: 100vw; height: 120px;
          background: linear-gradient(to top, rgba(147, 51, 234, 0.22), rgba(236, 72, 153, 0.06) 55%, transparent);
          pointer-events: none; z-index: 30;
          animation: globalSmokeDrift 10s ease-in-out infinite alternate;
        }
      `}</style>
      <div className="global-purple-smoke" aria-hidden="true" data-testid="global-purple-smoke" />
    </>
  );
};

function AppRouter() {
  const location = useLocation();
  // Detect the OAuth session_id in the URL fragment synchronously during render
  // (useLocation().hash is reactive; window.location.hash is not) so the callback
  // runs BEFORE any route or auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/beats" element={<BeatsPage />} />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/quote" element={<QuotePage />} />
        <Route path="/talent" element={<TalentPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/roster" element={<RosterPage />} />
        <Route path="/medusa" element={<MedusaRoomPage />} />
        <Route path="/medusa-guide" element={<MedusaGuidePage />} />
        <Route path="/medusa-terms" element={<MedusaTermsPage />} />
        <Route path="/medusa/piano" element={<PianoMasteryPage />} />
        <Route path="/medusa/academy" element={<AcademyPage />} />
        <Route path="/medusa/studio" element={<Studio3DPage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/vip-vault" element={<VipVaultPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/news" element={<PressPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pages/license-terms" element={<TermsPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
      {!isAdmin && <ChatWidget />}
      <GlobalHaze />
      <AppSplash />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay" aria-hidden="true" />
      <BrowserRouter>
        <SiteAuthProvider>
          <AppRouter />
        </SiteAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
